import 'bootstrap';
import { SetBootstrapVersion } from 'smg/bootstrap';
import { Browser } from 'smg/browser';
import { CreateFormSubmissionWithReCaptcha, ReCaptchaError } from 'smg/customer-api/formsubmissions';
import { GetPageRequestID } from 'smg/customer-api/pagerequests';
import * as session from 'smg/customer-api/session';
import * as errors from 'smg/errors';
import { BindFancybox } from 'smg/fancybox';
import { SetIconLibrary } from 'smg/icons';
import * as toast from 'smg/toast';

import * as carts from './carts';
import { Relref } from './hugo';

declare interface myWindow extends Window {
    getPageRequestID: typeof GetPageRequestID;
}
declare const window: myWindow;

window.getPageRequestID = GetPageRequestID;

errors.InitClientSideErrorLogging();

SetIconLibrary('fontAwesome5');
SetBootstrapVersion(5);
// Toasts stack bottom-center (design Toast); the toast itself keeps smg/toast's defaults.
toast.SetToastContainerClasses(['toast-container', 'position-fixed', 'bottom-0', 'start-50', 'translate-middle-x', 'p-3']);

BindFancybox();

// Pricing tables and add-on prices appear across sport layouts, shortcodes,
// and content pages (not a single page), so they're gated on selectors and
// lazy-loaded — pages without them never download the lineitems/Big closure.
if (document.querySelector('.pricing-table')) {
    void import('./pricing-table').then(pricingTable => {
        pricingTable.PricingTables();
    });
}
if (document.querySelector('.child-line-item-option-price,.child-line-item-price')) {
    void import('./addon-pricing').then(addonPricing => {
        addonPricing.ChildLineItemPrices();
    });
}

function initSession() {
    const $navAccountLink = document.querySelector<HTMLAnchorElement>('#nav-account-link');
    const $sidebarLoginLink = document.querySelector<HTMLAnchorElement>('#sidebar-login-link');
    const $sidebarRegisterLink = document.querySelector<HTMLAnchorElement>('#sidebar-register-link');

    if (session.GetLoginToken()) {
        session.Session().then(() => {
            if ($navAccountLink) {
                $navAccountLink.href = Relref('/account');
            }

            if ($sidebarLoginLink) {
                $sidebarLoginLink.innerHTML = '<i class="fas fa-user-circle"></i><br>My Account';
                $sidebarLoginLink.href = Relref('/account');
            }

            if ($sidebarRegisterLink) {
                $sidebarRegisterLink.innerHTML = '<i class="fas fa-sign-out-alt"></i><br>Logout';
                $sidebarRegisterLink.href = '#';
                $sidebarRegisterLink.classList.add('logout');
                $sidebarRegisterLink.addEventListener('click', ev => {
                    ev.preventDefault();
                    session.DeleteSession();
                    window.location.href = Relref('/');
                });
            }
        }).catch(() => {
            // session invalid, leave nav as login/register
        });
    }
}

function initNewsletter() {
    const $form = document.querySelector<HTMLFormElement>('#newsletter-form');
    if (!$form) {
        return;
    }

    const $email = $form.querySelector<HTMLInputElement>('#newsletter-email');
    const $btn = $form.querySelector<HTMLButtonElement>('#newsletter-btn');
    const $reCaptchaPlaceholder = $form.querySelector<HTMLElement>('.contact-recpatcha-parent');
    if (!$email || !$btn || !$reCaptchaPlaceholder) {
        return;
    }

    $form.addEventListener('submit', async e => {
        e.preventDefault();

        const email = $email.value.trim();
        if (!email) {
            return;
        }

        $btn.disabled = true;
        $btn.textContent = 'Subscribing...';

        try {
            // The signup persists as a `newsletter` form submission (#7260);
            // the routing automation does the user create/attach, so nothing
            // here waits on it.
            await CreateFormSubmissionWithReCaptcha({
                form: 'newsletter',
                variant: 'footer',
                payload: {
                    email,
                },
            }, {
                $reCaptchaPlaceholder,
                action: 'newsletter',
            });
            $form.innerHTML = '<p class="text-white mb-0"><i class="fas fa-check-circle me-2"></i>Thank you for subscribing!</p>';
        } catch (err) {
            $btn.disabled = false;
            $btn.textContent = 'Subscribe';
            if (err === ReCaptchaError) {
                errors.ShowError(err);
            } else {
                errors.ShowError('Something went wrong. Please try again.');
            }
        }
    });
}

async function main() {
    document.body.classList.add(`browser-${Browser()}`);

    // Everything that identifies the visitor waits for the ?loginToken= exchange,
    // or a page request could be recorded against the session about to be replaced.
    await session.ConsumeLoginTokenFromURL();
    void GetPageRequestID();

    initSession();
    initNewsletter();

    carts.GetCartCount().then(count => {
        if (count) {
            carts.IncrementCartCount(count);
        }
    }).catch(err => console.error(`failed to get cart count: ${String(err)}`));
}

void main();
